/**
 * Insights data access: TanStack Query hooks over the typed client. Other modules may use these
 * hooks (the web side of insights' service functions), never insights' pages.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type { Entity, Granularity, Period, ScoreKey } from './model';

export const insightsKeys = {
  all: ['insights'] as const,
  summary: (p: object) => ['insights', 'summary', p] as const,
  series: (p: object) => ['insights', 'series', p] as const,
  ranking: (p: object) => ['insights', 'ranking', p] as const,
  entity: (entity: Entity, id: string, p: object) => ['insights', 'entity', entity, id, p] as const,
  groups: (p: object) => ['insights', 'groups', p] as const,
  tree: ['insights', 'tree'] as const,
  dimensions: (applies?: string) => ['insights', 'dimensions', applies ?? 'all'] as const,
};

/** Scores change when day buckets are recomputed, not second by second. */
const STALE = 60_000;

export interface SummaryQuery extends Period {
  project?: string[];
  group?: string;
  mode?: 'membership' | 'current';
}

export function useSummary(p: SummaryQuery, enabled = true) {
  return useQuery({
    queryKey: insightsKeys.summary(p),
    queryFn: () =>
      unwrap(api.GET('/api/insights/summary', { params: { query: { start: p.start, end: p.end, project: p.project, group: p.group, mode: p.mode } } })),
    placeholderData: keepPreviousData,
    staleTime: STALE,
    enabled,
  });
}

export interface SeriesQuery extends Period {
  entity: 'project' | 'vehicle' | 'driver';
  id?: string[];
  project?: string[];
  metric: string[];
  granularity: Granularity;
}

export function useSeries(p: SeriesQuery, enabled = true) {
  return useQuery({
    queryKey: insightsKeys.series(p),
    queryFn: () =>
      unwrap(
        api.GET('/api/insights/series', {
          params: {
            query: { entity: p.entity, id: p.id, project: p.project, metric: p.metric, start: p.start, end: p.end, granularity: p.granularity },
          },
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: STALE,
    enabled,
  });
}

export interface RankingQuery extends Period {
  entity: Entity;
  sort: ScoreKey;
  project?: string[];
  group?: string[];
  minKm: number;
  cohort?: string;
  limit?: number;
}

export function useRanking(p: RankingQuery, enabled = true) {
  return useQuery({
    queryKey: insightsKeys.ranking(p),
    queryFn: () =>
      unwrap(
        api.GET('/api/insights/ranking', {
          params: {
            query: {
              entity: p.entity,
              sort: p.sort,
              start: p.start,
              end: p.end,
              project: p.project,
              group: p.group,
              min_km: p.minKm,
              cohort: p.cohort,
              limit: p.limit ?? 1000,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: STALE,
    enabled,
  });
}

export function useEntityPerformance(entity: Entity, id: string, p: Period, enabled = true) {
  return useQuery({
    queryKey: insightsKeys.entity(entity, id, p),
    queryFn: () =>
      unwrap(
        api.GET('/api/insights/entity/{entity}/{entity_id}', {
          params: { path: { entity, entity_id: id }, query: { start: p.start, end: p.end } },
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: STALE,
    enabled: enabled && !!id,
  });
}

export interface GroupsQuery extends Period {
  dimension: string;
  mode?: 'membership' | 'current';
}

export function useGroupComparison(p: GroupsQuery, enabled = true) {
  return useQuery({
    queryKey: insightsKeys.groups(p),
    queryFn: () => unwrap(api.GET('/api/insights/groups', { params: { query: { dimension: p.dimension, start: p.start, end: p.end, mode: p.mode } } })),
    placeholderData: keepPreviousData,
    staleTime: STALE,
    enabled: enabled && !!p.dimension,
  });
}

/** The nova-v1 tree (keys, parents, exposure minimums) — fixed per algorithm version. */
export function useScoreTree() {
  return useQuery({ queryKey: insightsKeys.tree, queryFn: () => unwrap(api.GET('/api/insights/score-tree')), staleTime: Infinity });
}

export function useDimensions(appliesTo?: 'vehicle' | 'driver', enabled = true) {
  return useQuery({
    queryKey: insightsKeys.dimensions(appliesTo),
    queryFn: () => unwrap(api.GET('/api/insights/dimensions', { params: { query: { applies_to: appliesTo } } })),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** Re-run the automatic classifiers (vehicle type, duty profile, work type, operating area). */
export function useClassifyNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/insights/classify')),
    onSuccess: () => qc.invalidateQueries({ queryKey: insightsKeys.all }),
  });
}
